/**
 * Task 151-D — monthly package entitlement progress (client Services
 * tab). Fetches `get-client-package-progress` and renders, per active
 * package service, the current month's entitlement / carried-in /
 * consumed / remaining / expiring carry-over. All numbers come from the
 * server (snapshot entitlement + deliveries ledger) — never UI counters.
 */

import { useEffect, useState } from 'react';
import { getClientPackageProgress } from '../../api/endpoints';
import type {
  ClientPackageAssignmentProgress,
  ClientPackageServiceProgress,
  SystemCapabilityKey,
} from '../../api/types';
import { capabilityLabelAr } from '../../permissions';
import { ErrorState, LoadingState } from '../../components/states';
import { selectProgressView } from './progressView';

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly progress: ReadonlyArray<ClientPackageAssignmentProgress> }
  | { readonly status: 'error'; readonly error: unknown };

export function PackageProgressSection({
  clientId,
  reloadSignal,
}: {
  readonly clientId: string;
  /** Bump from a parent (e.g. after recording a delivery) to refetch. */
  readonly reloadSignal?: number;
}) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    getClientPackageProgress({ clientId, signal: ctrl.signal })
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', progress: d.progress });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey, reloadSignal]);

  if (state.status === 'loading') return <LoadingState title="جاري تحميل تقدّم الباقة…" />;
  if (state.status === 'error')
    return (
      <ErrorState
        title="تعذّر تحميل تقدّم الباقة"
        error={state.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );

  // Keep the section VISIBLE whenever the client has an active assignment.
  // Detailed rows show for tracked (non-zero) services; an active package
  // with no monthly entitlement / zero deliveries renders a zero/empty
  // state instead of disappearing. Return null only with no active
  // assignment at all.
  const { visible, tracked, month } = selectProgressView(state.progress);
  if (!visible) return null;

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>تقدّم الباقة هذا الشهر</h2>
        <span className="meta">{month}</span>
      </div>
      {tracked.length > 0 ? (
        <>
          <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 14 }}>
            المستحق الشهري لكل خدمة مقابل ما تم تنفيذه. المرحّل من الشهر السابق يُستهلك أولاً وينتهي
            نهاية هذا الشهر إن لم يُستخدم.
          </p>
          {tracked.map((a) => (
            <div key={a.assignment_id} style={{ marginBottom: 16 }}>
              {a.package_name ? (
                <div className="lbl" style={{ marginBottom: 8 }}>
                  {a.package_name}
                </div>
              ) : null}
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {a.services.map((s) => (
                  <ServiceProgressRow key={s.service_id} service={s} />
                ))}
              </ul>
            </div>
          ))}
        </>
      ) : (
        <p className="tiny" style={{ color: 'var(--muted)', margin: 0 }}>
          لا توجد خدمات ذات مستحق شهري في الباقة الحالية بعد.
        </p>
      )}
    </section>
  );
}

function ServiceProgressRow({ service }: { readonly service: ClientPackageServiceProgress }) {
  const total = service.entitlement + service.carried_in;
  const pct = total > 0 ? Math.min(100, Math.round((service.consumed / total) * 100)) : 0;
  return (
    <li
      style={{
        padding: 12,
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>{service.service_name}</strong>
        {service.capability_key ? (
          <span className="pill pill-info" style={{ fontSize: 10 }}>
            {capabilityLabelAr(service.capability_key as SystemCapabilityKey)}
          </span>
        ) : null}
      </div>
      {/* Progress bar: consumed of (entitlement + carried-in). */}
      <div
        style={{
          height: 8,
          borderRadius: 'var(--r-full)',
          background: 'var(--cream-2)',
          overflow: 'hidden',
          margin: '8px 0',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--tangerine)',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          fontSize: 12,
          color: 'var(--ink-2)',
        }}
      >
        <span>
          المستحق الشهري: <strong dir="ltr">{service.entitlement}</strong>
        </span>
        {service.carried_in > 0 ? (
          <span>
            مُرحّل: <strong dir="ltr">{service.carried_in}</strong>
          </span>
        ) : null}
        <span>
          مُنفّذ: <strong dir="ltr">{service.consumed}</strong>
        </span>
        <span>
          متبقّي: <strong dir="ltr">{service.remaining}</strong>
        </span>
        {service.expiring > 0 ? (
          <span style={{ color: 'var(--rose)' }}>
            ينتهي نهاية الشهر: <strong dir="ltr">{service.expiring}</strong>
          </span>
        ) : null}
      </div>
    </li>
  );
}
